import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, Row, Col, Button, Tag, Progress, Select, Spin, Empty } from "antd";
import {
  ExclamationCircleOutlined,
  UserOutlined,
  SendOutlined,
  MailOutlined,
  DatabaseOutlined,
  ArrowRightOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import styles from "./CommercialIntelligenceDashboard.module.scss";
import {
  getCommercialIntelligenceDashboard,
  getCommercialIntelligenceFeaturedCampaign,
  convertActivationActionToCampaign,
} from "../../../api/Services";
import {
  CommercialIntelligenceDashboardData,
  CIAlert,
  CIOpportunityGroup,
  FeaturedCampaignData,
} from "../../../types/commercialIntelligenceDashboard";
import { formatRecommendationMessage } from "../../../helpers/RecommendationMessageHelper";

type OpportunityTab = "incentives" | "promotions" | "communications";

const FEATURED_PERIODS = [
  "LAST_30_DAYS",
  "LAST_3_MONTHS",
  "LAST_6_MONTHS",
  "LAST_YEAR",
] as const;

const CommercialIntelligenceDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [opportunityTab, setOpportunityTab] = useState<OpportunityTab>("incentives");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CommercialIntelligenceDashboardData | null>(null);

  const [featuredPeriod, setFeaturedPeriod] = useState<string>("LAST_30_DAYS");
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [featured, setFeatured] = useState<FeaturedCampaignData | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getCommercialIntelligenceDashboard().then((res) => {
      if (active) {
        setData(res);
        setLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setFeaturedLoading(true);
    getCommercialIntelligenceFeaturedCampaign(featuredPeriod).then((res) => {
      if (active) {
        setFeatured(res);
        setFeaturedLoading(false);
      }
    });
    return () => {
      active = false;
    };
  }, [featuredPeriod]);

  const formatCurrency = (amount: number) => {
    const formatted = new Intl.NumberFormat("es-AR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
    return `$${formatted}`;
  };

  const renderCampaignType = (type?: string | null) => {
    if (!type) return "-";
    if (type === "INCENTIVE") return t("Common-CampaignTypeIncentive");
    if (type === "PROMOTION") return t("Common-CampaignTypePromotion");
    if (type === "SEGMENT") return t("Common-CampaignTypeSegment");
    if (type === "ACTIVATION") return t("Common-CampaignTypeActivation");
    return type;
  };

  const formatRoi = (roi?: number | null) =>
    roi === null || roi === undefined ? "-" : `ROI ${roi}`;

  const dataHealthMetrics = useMemo(() => {
    if (!data) return [];
    const dh = data.dataHealth;
    return [
      {
        id: 1,
        title: t("CommercialIntelligence-DataHealthContactable"),
        percentage: dh.contactablePct ?? 0,
        description: t("CommercialIntelligence-DataHealthContactableHint"),
        icon: <UserOutlined />,
      },
      {
        id: 2,
        title: t("CommercialIntelligence-DataHealthMobile"),
        percentage: dh.mobilePct ?? 0,
        description: t("CommercialIntelligence-DataHealthMobileHint"),
        icon: <SendOutlined />,
      },
      {
        id: 3,
        title: t("CommercialIntelligence-DataHealthEmail"),
        percentage: dh.emailPct ?? 0,
        description: t("CommercialIntelligence-DataHealthEmailHint"),
        icon: <MailOutlined />,
      },
      {
        id: 4,
        title: t("CommercialIntelligence-DataHealthHistory"),
        percentage: dh.sufficientHistoryPct ?? 0,
        description: t("CommercialIntelligence-DataHealthHistoryHint"),
        icon: <DatabaseOutlined />,
      },
    ];
  }, [data, t]);

  const alertMessage = (alert: CIAlert) => {
    const pct = alert.percentage;
    if (alert.type === "NO_CONTACT")
      return t("CommercialIntelligence-AlertNoContact", { pct });
    if (alert.type === "TICKETS_NO_CLIENT")
      return t("CommercialIntelligence-AlertTicketsNoClient", { pct });
    return "";
  };

  const alertType = (alert: CIAlert) =>
    alert.type === "NO_CONTACT" ? "warning" : "info";

  const getAlertColor = (type: string) =>
    type === "warning" ? "#faad14" : "#d9d9d9";

  const getAlertIcon = (type: string) =>
    type === "warning" ? <ExclamationCircleOutlined /> : <InfoCircleOutlined />;

  const opportunityGroup: CIOpportunityGroup | null = data
    ? data[opportunityTab]
    : null;

  const resolveOpportunityName = (opportunity: {
    name: string;
    nameKey?: string | null;
    nameParams?: Record<string, string | number> | null;
  }) =>
    formatRecommendationMessage(
      t,
      opportunity.nameKey,
      opportunity.nameParams,
      opportunity.name,
    );

  const handleOpportunityAction = async (
    id: number,
    opportunity: {
      name: string;
      nameKey?: string | null;
      nameParams?: Record<string, string | number> | null;
      estimatedImpact?: number | null;
    },
  ) => {
    const name = resolveOpportunityName(opportunity);
    if (opportunityTab === "incentives") {
      navigate("/commercial-intelligence/campaigns/create", {
        state: {
          rewardId: id,
          rewardName: name,
          estimatedImpact: opportunity.estimatedImpact ?? undefined,
        },
      });
    } else if (opportunityTab === "promotions") {
      navigate("/commercial-intelligence/campaigns/create", {
        state: { promotionId: id, promotionName: name },
      });
    } else {
      const result = await convertActivationActionToCampaign(id);
      if (result.success && result.data?.campaignId) {
        navigate(
          `/commercial-intelligence/campaigns/${result.data.campaignId}/view`,
        );
      }
    }
  };

  const opportunitiesViewLink = () => {
    if (opportunityTab === "incentives") {
      return {
        label: t("CommercialIntelligence-ViewIncentives"),
        path: "/commercial-intelligence/opportunities/incentives",
      };
    }
    if (opportunityTab === "promotions") {
      return {
        label: t("CommercialIntelligence-ViewPromotions"),
        path: "/commercial-intelligence/opportunities/promotions",
      };
    }
    return {
      label: t("CommercialIntelligence-ViewCommunications"),
      path: "/commercial-intelligence/opportunities/activation-actions",
    };
  };

  const periodLabel = (period: string) => {
    switch (period) {
      case "LAST_3_MONTHS":
        return t("CommercialIntelligence-PeriodLast3Months");
      case "LAST_6_MONTHS":
        return t("CommercialIntelligence-PeriodLast6Months");
      case "LAST_YEAR":
        return t("CommercialIntelligence-PeriodLastYear");
      default:
        return t("CommercialIntelligence-PeriodLast30Days");
    }
  };

  const isCommunications = opportunityTab === "communications";

  return (
    <div className={styles.dashboardWrapper}>
      <div className={styles.contentWrapper}>
        <Spin spinning={loading}>
          <Row gutter={[16, 16]} className={styles.mainContent}>
            <Col xs={24} lg={12}>
              <Card className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>
                    {t("CommercialIntelligence-DataHealth")}
                  </h3>
                </div>
                <Row gutter={[12, 12]}>
                  {dataHealthMetrics.map((metric) => (
                    <Col xs={24} sm={12} key={metric.id}>
                      <Card className={styles.metricCard}>
                        <div className={styles.metricIcon}>{metric.icon}</div>
                        <div className={styles.metricContent}>
                          <h4 className={styles.metricTitle}>{metric.title}</h4>
                          <div className={styles.metricPercentage}>
                            {metric.percentage}%
                          </div>
                          <p className={styles.metricDescription}>
                            {metric.description}
                          </p>
                          <Progress
                            percent={Number(metric.percentage)}
                            showInfo={false}
                            strokeColor="#1890ff"
                            className={styles.metricProgress}
                          />
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>

                <div className={styles.alertsSection}>
                  <div className={styles.sectionHeader}>
                    <h3 className={styles.sectionTitle}>
                      {t("CommercialIntelligence-Alerts")}
                    </h3>
                    <Button
                      type="link"
                      className={styles.sectionActionButton}
                      onClick={() => navigate("/commercial-intelligence/clients")}
                    >
                      {t("CommercialIntelligence-ViewDataHealth")}
                    </Button>
                  </div>
                  <div className={styles.alertsList}>
                    {(data?.alerts ?? []).map((alert) => (
                      <div key={alert.type} className={styles.alertItem}>
                        <Tag
                          icon={getAlertIcon(alertType(alert))}
                          color={getAlertColor(alertType(alert))}
                          className={styles.alertTag}
                        >
                          {alertType(alert) === "warning"
                            ? t("CommercialIntelligence-Attention")
                            : t("Common-Info")}
                        </Tag>
                        <span className={styles.alertMessage}>
                          {alertMessage(alert)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>
                    {t("CommercialIntelligence-Opportunities")}
                  </h3>
                </div>
                <div className={styles.opportunityTabs}>
                  <Button
                    type={opportunityTab === "incentives" ? "primary" : "default"}
                    className={styles.tabButton}
                    onClick={() => setOpportunityTab("incentives")}
                  >
                    {t("CommercialIntelligence-Incentives")}
                  </Button>
                  <Button
                    type={opportunityTab === "promotions" ? "primary" : "default"}
                    className={styles.tabButton}
                    onClick={() => setOpportunityTab("promotions")}
                  >
                    {t("CommercialIntelligence-Promotions")}
                  </Button>
                  <Button
                    type={opportunityTab === "communications" ? "primary" : "default"}
                    className={styles.tabButton}
                    onClick={() => setOpportunityTab("communications")}
                  >
                    {t("CommercialIntelligence-Communications")}
                  </Button>
                  <Button
                    type="link"
                    className={styles.sectionActionButton}
                    onClick={() => navigate(opportunitiesViewLink().path)}
                  >
                    {opportunitiesViewLink().label}
                  </Button>
                </div>
                <div className={styles.opportunitySummary}>
                  <div className={styles.opportunityStat}>
                    <span className={styles.opportunityStatLabel}>
                      {t("CommercialIntelligence-Suggested")}
                    </span>
                    <span className={styles.opportunityStatValue}>
                      {opportunityGroup?.suggestedCount ?? 0}
                    </span>
                  </div>
                  <div className={styles.opportunityStat}>
                    <span className={styles.opportunityStatLabel}>
                      {t("CommercialIntelligence-EstimatedImpact")}
                    </span>
                    <span className={styles.opportunityStatValue}>
                      {opportunityGroup?.totalEstimatedImpact != null
                        ? formatCurrency(opportunityGroup.totalEstimatedImpact)
                        : "-"}
                    </span>
                  </div>
                </div>
                <div className={styles.opportunitiesList}>
                  {opportunityGroup && opportunityGroup.items.length > 0 ? (
                    opportunityGroup.items.map((opportunity) => (
                      <div key={opportunity.id} className={styles.opportunityCard}>
                        <div className={styles.opportunityCardContent}>
                          <h4 className={styles.opportunityCardTitle}>
                            {resolveOpportunityName(opportunity)}
                          </h4>
                          <p className={styles.opportunityCardImpact}>
                            {isCommunications
                              ? `${t("CommercialIntelligence-EstimatedImpact")}: ${
                                  opportunity.estimatedImpact != null
                                    ? formatCurrency(opportunity.estimatedImpact)
                                    : "-"
                                } · ${t("ActivationAction-EstimatedConversion")}: ${
                                  opportunity.estimatedConversion ?? "-"
                                }`
                              : opportunity.estimatedImpact != null
                              ? `${t("CommercialIntelligence-EstimatedImpact")}: ${formatCurrency(
                                  opportunity.estimatedImpact
                                )}`
                              : t("CommercialIntelligence-EstimatedImpact")}
                          </p>
                        </div>
                        <Button
                          type="default"
                          className={styles.actionButton}
                          onClick={() =>
                            handleOpportunityAction(opportunity.id, opportunity)
                          }
                        >
                          {t("CommercialIntelligence-Action")}
                        </Button>
                      </div>
                    ))
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={t("CommercialIntelligence-NoOpportunities")}
                    />
                  )}
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} className={styles.bottomContent}>
            <Col xs={24} lg={16}>
              <Card className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>
                    {t("CommercialIntelligence-ActiveCampaigns")}
                  </h3>
                  <Button
                    type="link"
                    className={styles.sectionActionButton}
                    onClick={() => navigate("/commercial-intelligence/campaigns")}
                  >
                    {t("CommercialIntelligence-ViewCampaigns")}
                  </Button>
                </div>
                <div className={styles.campaignsList}>
                  {data && data.activeCampaigns.length > 0 ? (
                    data.activeCampaigns.map((campaign) => (
                      <div key={campaign.id} className={styles.campaignItem}>
                        <div className={styles.campaignContent}>
                          <h4 className={styles.campaignName}>{campaign.name}</h4>
                          <p className={styles.campaignMeta}>
                            {renderCampaignType(campaign.type)}
                          </p>
                        </div>
                        <div className={styles.campaignMetrics}>
                          <span className={styles.campaignMetric}>
                            {formatRoi(campaign.roi)}
                          </span>
                          <Button
                            type="link"
                            className={styles.openButton}
                            onClick={() =>
                              navigate(
                                `/commercial-intelligence/campaigns/${campaign.id}/view`
                              )
                            }
                          >
                            {t("CommercialIntelligence-Open")}
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={t("CommercialIntelligence-NoActiveCampaigns")}
                    />
                  )}
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>
                    {t("CommercialIntelligence-FeaturedResult")}
                  </h3>
                  <Select
                    value={featuredPeriod}
                    onChange={setFeaturedPeriod}
                    size="small"
                    className={styles.periodSelect}
                    options={FEATURED_PERIODS.map((p) => ({
                      value: p,
                      label: periodLabel(p),
                    }))}
                  />
                </div>
                <Spin spinning={featuredLoading}>
                  {featured && featured.found ? (
                    <>
                      <Card className={styles.featuredCard}>
                        <div className={styles.featuredLabel}>
                          {t("CommercialIntelligence-Campaign")}
                        </div>
                        <h4 className={styles.featuredCampaignName}>
                          {featured.name}
                        </h4>
                        <p className={styles.featuredImpact}>
                          {t("CommercialIntelligence-FeaturedSales")}:{" "}
                          {formatCurrency(featured.incrementalSales ?? 0)} •{" "}
                          {formatRoi(featured.roi)}
                        </p>
                      </Card>
                      <Button
                        type="primary"
                        className={styles.viewCampaignButton}
                        onClick={() =>
                          navigate(
                            `/commercial-intelligence/campaigns/${featured.id}/view`
                          )
                        }
                        icon={<ArrowRightOutlined />}
                      >
                        {t("CommercialIntelligence-ViewCampaign")}
                      </Button>
                    </>
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={t("CommercialIntelligence-NoFeaturedResult")}
                    />
                  )}
                </Spin>
              </Card>
            </Col>
          </Row>

          <Row justify="center" className={styles.createButtonsRow}>
            <Col xs={24} sm={24} md={20} lg={16} xl={12}>
              <div className={styles.createButtonsSection}>
                <Button
                  type="primary"
                  className={styles.createButton}
                  onClick={() => navigate("/commercial-intelligence/campaigns/create")}
                >
                  {t("CommercialIntelligence-CreateCampaign")}
                  <ArrowRightOutlined />
                </Button>
                <Button
                  type="primary"
                  className={styles.createButton}
                  onClick={() =>
                    navigate("/commercial-intelligence/opportunities/promotions/create")
                  }
                >
                  {t("CommercialIntelligence-CreatePromotion")}
                  <ArrowRightOutlined />
                </Button>
                <Button
                  type="primary"
                  className={styles.createButton}
                  onClick={() =>
                    navigate("/commercial-intelligence/opportunities/incentives/create")
                  }
                >
                  {t("CommercialIntelligence-CreateIncentive")}
                  <ArrowRightOutlined />
                </Button>
              </div>
            </Col>
          </Row>
        </Spin>
      </div>
    </div>
  );
};

export default CommercialIntelligenceDashboard;
